import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, select, takeLatest } from 'redux-saga/effects';
import { setAllocationFileRecordIds } from 'redux/slice/allocation/AllocationDetails.slice';
import {
  addAlternateNumber,
  addAlternateNumberFailure,
  addAlternateNumberSuccess,
  addDisposition,
  addDispositionFailure,
  addDispositionSuccess,
  callBarging,
  callBargingFailure,
  callBargingSuccess,
  clearAcwStartTime,
  clickToCall,
  clickToCallSuccess,
  fetchAdditionalInfo,
  fetchAdditionalInfoFailure,
  fetchAdditionalInfoSuccess,
  fetchAgentInfo,
  fetchAgentInfoFailure,
  fetchAgentInfoSuccess,
  fetchBestAndLastDisposition,
  fetchBestAndLastDispositionFailure,
  fetchBestAndLastDispositionSuccess,
  fetchBorrowerInfo,
  fetchBorrowerInfoFailure,
  fetchBorrowerInfoSuccess,
  fetchBorrowersInCurrentCampaign,
  fetchBorrowersInCurrentCampaignFailure,
  fetchBorrowersInCurrentCampaignSuccess,
  fetchCampaignsByAgent,
  fetchCampaignsByAgentFailure,
  fetchCampaignsByAgentSuccess,
  fetchDispositionWiseSummary,
  fetchDispositionWiseSummaryFailure,
  fetchDispositionWiseSummarySuccess,
  fetchDispositions,
  fetchDispositionsFailure,
  fetchDispositionsSuccess,
  fetchFollowUpCurrentTime,
  fetchFollowUpCurrentTimeFailure,
  fetchFollowUpCurrentTimeSuccess,
  fetchFollowUpTimeRanges,
  fetchFollowUpTimeRangesFailure,
  fetchFollowUpTimeRangesSuccess,
  fetchInteractionHistory,
  fetchInteractionHistoryFailure,
  fetchInteractionHistorySuccess,
  fetchMissedCalls,
  fetchMissedCallsDistinct,
  fetchMissedCallsDistinctFailure,
  fetchMissedCallsDistinctSuccess,
  fetchMissedCallsFailure,
  fetchMissedCallsSuccess,
  fetchPreviousOrNextBorrower,
  fetchPreviousOrNextBorrowerFailure,
  fetchPreviousOrNextBorrowerSuccess,
  fetchPriorityTasks,
  fetchPriorityTasksDistinct,
  fetchPriorityTasksDistinctFailure,
  fetchPriorityTasksDistinctSuccess,
  fetchPriorityTasksFailure,
  fetchPriorityTasksSuccess,
  fetchPriorityTasksSummary,
  fetchPriorityTasksSummaryFailure,
  fetchPriorityTasksSummarySuccess,
  fetchWhatsappBadgeCount,
  fetchWhatsappBadgeCountFailure,
  fetchWhatsappBadgeCountSuccess,
  fetchWhatsappMessages,
  fetchWhatsappMessagesFailure,
  fetchWhatsappMessagesSuccess,
  getRemarkTemplates,
  getRemarkTemplatesFailure,
  getRemarkTemplatesSuccess,
  getUniqueValuesByColumnName,
  getUniqueValuesByColumnNameFailure,
  getUniqueValuesByColumnNameSuccess,
  navigateTo,
  resetActivityHistoryFilters,
  saveCampaign,
  saveCampaignFailure,
  saveCampaignSuccess,
  searchInCrmDataGrid,
  searchInCrmDataGridFailure,
  searchInCrmDataGridSuccess,
  searchInFilterByValues,
  searchInFilterByValuesFailure,
  searchInFilterByValuesSuccess,
  setCurrentCampaign,
  setIsAgentInManualCall,
  setLatestSubDispositionId,
  setShowIframe,
  updateAlternateNumber,
  updateAlternateNumberFailure,
  updateAlternateNumberSuccess,
} from 'redux/slice/crm/CRM.slice';
import { setUniqueValues } from 'redux/slice/filter/Filter.slice';
import { RootReducerState } from 'redux/slice/reducer';
import { trigger as triggerErrorModal } from 'redux/slice/ui/ErrorModal.slice';
import {
  IAddAlternateNumberRequest,
  IAddAlternateNumberResponse,
  IAddDispositionRequest,
  ICallBargingRequest,
  ICallBargingResponse,
  IClickToCallRequest,
  IClickToCallResponse,
  IFetchAdditionalInfoRequest,
  IFetchAdditionalInfoResponse,
  IFetchBestAndLastDispositionRequest,
  IFetchBestAndLastDispositionResponse,
  IFetchBorrowerInfoRequest,
  IFetchBorrowerInfoResponse,
  IFetchBorrowersInCurrentCampaignRequest,
  IFetchBorrowersInCurrentCampaignResponse,
  IFetchCampaignsByAgentRequest,
  IFetchCampaignsByAgentResponse,
  IFetchDispositionWiseSummaryRequest,
  IFetchDispositionWiseSummaryResponse,
  IFetchDispositionsResponse,
  IFetchFollowUpTimeRangesRequest,
  IFetchFollowUpTimeRangesResponse,
  IFetchInteractionHistoryRequest,
  IFetchInteractionHistoryResponse,
  IFetchMissedCallsDistinctRequest,
  IFetchMissedCallsRequest,
  IFetchMissedCallsResponse,
  IFetchPreviousOrNextBorrowerRequest,
  IFetchPreviousOrNextBorrowerResponse,
  IFetchPriorityTasksDistinctRequest,
  IFetchPriorityTasksRequest,
  IFetchPriorityTasksResponse,
  IFetchPriorityTasksSummaryRequest,
  IFetchWhatsappBadgeCountResponse,
  IFetchWhatsappMessagesRequest,
  IGetRemarkTemplatesResponse,
  IGetUniqueValuesByColumnNameRequest,
  ISaveCampaignRequest,
  ISaveCampaignResponse,
  ISearchInCrmDataGridRequest,
  ISearchInFilterByValuesRequest,
  ISearchInFilterByValuesResponse,
  IUpdateAlternateNumberRequest,
  IUpdateAlternateNumberResponse,
  IWhatsappMessagesResponse,
  MissedCallDistinctItemWithId,
  PriorityTasksSummary,
} from 'redux/types/CRM.types';
import { crmEndPoints, whatsappEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import { handleError } from '../utils/errorUtils';

// Fetch campaigns by agent
function* fetchCampaignsByAgentSaga(
  action: PayloadAction<IFetchCampaignsByAgentRequest>
) {
  try {
    const { agentId } = action.payload;
    const response: AxiosResponse<IFetchCampaignsByAgentResponse[]> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.FETCH_CAMPAIGNS_BY_AGENT(agentId)}`
      );
    yield put(fetchCampaignsByAgentSuccess(response.data));
    const lastSelectedCampaign = response.data.filter(
      (campaign) => campaign.lastSelectedCampaign === true
    );
    if (lastSelectedCampaign.length <= 0) return;
    yield put(setCurrentCampaign(lastSelectedCampaign[0]));
    // message.success('Campaigns fetched successfully');
  } catch (error: any) {
    const payload = {
      error: {
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to fetch campaigns',
        details: error?.response?.data?.details,
      },
    };
    yield put(fetchCampaignsByAgentFailure(payload));
  }
}
// Save campaign
function* saveCampaignSaga(action: PayloadAction<ISaveCampaignRequest>) {
  try {
    const { allocatorId, campaignId, isMissedCall } = action.payload;
    const response: AxiosResponse<ISaveCampaignResponse> =
      yield HTTPClient.PostRequest(`${crmEndPoints.SAVE_CAMPAIGN({})}`, {
        isMissedCall,
        allocatorId,
        campaignId,
      });
    yield put(saveCampaignSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, saveCampaignFailure, '');
  }
}

// Fetch borrowers in current campaign
function* fetchBorrowersInCurrentCampaignSaga(
  action: PayloadAction<IFetchBorrowersInCurrentCampaignRequest>
) {
  try {
    const {
      agentId,
      campaignId,
      followUp,
      expired,
      startHour,
      endHour,
      limit = 10,
      offset = 0,
      columns,
      dispositionIds,
      untouched,
    } = action.payload;
    const response: AxiosResponse<IFetchBorrowersInCurrentCampaignResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.FETCH_BORROWERS_IN_CURRENT_CAMPAIGN(
          agentId,
          campaignId,
          limit,
          offset
        )}`,
        {
          filters: { columns },
          dispositionIds,
          followUp,
          expired,
          startHour,
          endHour,
          ...(untouched !== undefined && { untouched }),
        }
      );
    yield put(fetchBorrowersInCurrentCampaignSuccess(response.data));
  } catch (error: any) {
    const payload = {
      error: {
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to fetch campaigns',
        details: error?.response?.data?.details,
      },
    };
    yield put(fetchBorrowersInCurrentCampaignFailure(payload));
  }
}

// Saga for getting unique values by column name
function* getUniqueValuesByColumnNameSaga(
  action: PayloadAction<IGetUniqueValuesByColumnNameRequest>
) {
  const {
    columnName,
    agentId,
    campaignId,
    columns,
    limit,
    offset,
    followUp,
    expired,
    dispositionIds,
  } = action.payload;
  try {
    const response: AxiosResponse<string[]> = yield HTTPClient.PostRequest(
      `${crmEndPoints.GET_UNIQUE_VALUES_BY_COLUMN(
        agentId,
        campaignId,
        columnName,
        limit,
        offset
      )}`,
      {
        filters: { columns },
        followUp,
        expired,
        dispositionIds,
      }
    );

    yield put(getUniqueValuesByColumnNameSuccess(response.data));
    yield put(setUniqueValues(response.data));
    // Special handling for allocationRecordId
    if (columnName === 'id') {
      yield put(setAllocationFileRecordIds(response.data));
    }
    // message.success('Unique values fetched successfully');
  } catch (error: any) {
    yield put(getUniqueValuesByColumnNameFailure(error?.message));

    // Get error message and details
    const errorMessage =
      error?.response?.data?.message || 'Something went wrong!';
    const errorDetails = error?.response?.data?.details || 'Please try again';

    yield put(
      triggerErrorModal({
        message: errorMessage,
        subMessage: errorDetails,
      })
    );
  }
}

// Saga for searching in filter by values
function* searchInFilterByValuesSaga(
  action: PayloadAction<ISearchInFilterByValuesRequest>
) {
  try {
    const { columnName, columnType, columnValue, allocationId } =
      action.payload;
    const response: AxiosResponse<ISearchInFilterByValuesResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.SEARCH_IN_FILTER_BY_VALUES(allocationId)}`,
        { columnName, columnType, columnValue }
      );
    yield put(searchInFilterByValuesSuccess(response.data));
    yield put(setUniqueValues(response.data.data));
    // message.success('Search results fetched successfully');
  } catch (error: any) {
    yield put(searchInFilterByValuesFailure(error?.message));
    message.error('Failed to fetch search results');
  }
}

// Fetch search borrowers in current campaign
function* searchInCrmDataGridSaga(
  action: PayloadAction<ISearchInCrmDataGridRequest>
) {
  try {
    const {
      agentId,
      campaignId,
      searchValue,
      limit = 10,
      offset = 0,
      columns = [],
      dispositionIds,
      followUp,
      expired,
    } = action.payload;
    const response: AxiosResponse<IFetchBorrowersInCurrentCampaignResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.FETCH_SEARCHED_BORROWERS_IN_CURRENT_CAMPAIGN(
          agentId,
          campaignId,
          limit,
          offset,
          searchValue
        )}`,
        {
          filters: { columns },
          dispositionIds,
          followUp,
          expired,
        }
      );
    yield put(searchInCrmDataGridSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, searchInCrmDataGridFailure, '');
  }
}

// Click-to-call
function* clickToCallSaga(action: PayloadAction<IClickToCallRequest>) {
  try {
    const {
      agentId,
      campaignId,
      borrowerId,
      agentVerificationKey,
      alternateNo,
      isMissedCallNumber,
      missedCallLogID,
    } = action.payload;
    const params = new URLSearchParams();
    if (agentId) params.append('agentId', agentId);
    if (campaignId) params.append('campaignId', campaignId);
    if (borrowerId) params.append('borrowerId', borrowerId);
    if (agentVerificationKey)
      params.append('agentVerificationKey', agentVerificationKey);
    if (isMissedCallNumber)
      params.append('isMissedCallNumber', isMissedCallNumber.toString());
    if (missedCallLogID) params.append('missedCallLogID', missedCallLogID);

    let postData;
    if (alternateNo !== undefined) {
      postData = {
        alternateNumber: alternateNo,
      };
    }
    const response: AxiosResponse<IClickToCallResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.CLICK_TO_CALL()}?${params.toString()}`,
        postData
      );

    yield put(clickToCallSuccess(response.data));

    // Navigate to borrower details page after successful API call
    if (borrowerId && campaignId) {
      yield put(
        navigateTo({
          path: '/crm/borrower-details',
          params: { borrowerId, campaignId },
        })
      );
    }
    // message.success('Call initiated successfully');
  } catch (error: any) {
    // yield* handleError(error, clickToCallFailure, '');
    message.error('The agent is not available.');
  } finally {
    yield put(setShowIframe(true));
  }
}

// Fetch borrower info
function* fetchBorrowerInfoSaga(
  action: PayloadAction<IFetchBorrowerInfoRequest>
) {
  try {
    const { borrowerId, pin = '' } = action.payload;
    const response: AxiosResponse<IFetchBorrowerInfoResponse> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.FETCH_BORROWER_INFO(borrowerId, pin)}`
      );
    yield put(fetchBorrowerInfoSuccess(response.data));
    // message.success('Borrower info fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchBorrowerInfoFailure, '');
  }
}

// Fetch additional info
function* fetchAdditionalInfoSaga(
  action: PayloadAction<IFetchAdditionalInfoRequest>
) {
  try {
    const { allocationRecordId, allocationId } = action.payload;
    const response: AxiosResponse<IFetchAdditionalInfoResponse> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.FETCH_ADDITIONAL_DETAILS(
          allocationRecordId,
          allocationId
        )}`
      );
    yield put(fetchAdditionalInfoSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchAdditionalInfoFailure, '');
  }
}

// Fetch borrower info
function* fetchBestAndLastDispositionsSaga(
  action: PayloadAction<IFetchBestAndLastDispositionRequest>
) {
  try {
    const { borrowerId, entityId } = action.payload;
    const response: AxiosResponse<IFetchBestAndLastDispositionResponse> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.FETCH_BEST_AND_LAST_DISPOSITION(entityId, borrowerId)}`
      );

    yield put(fetchBestAndLastDispositionSuccess(response.data));
    // message.success('Best and Last disposition details fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchBestAndLastDispositionFailure, '');
  }
}

// Fetch agent info
function* fetchAgentInfoSaga(action: PayloadAction<{ agentId: string }>) {
  try {
    const { agentId } = action.payload;
    const response: AxiosResponse<IFetchBorrowerInfoResponse> =
      yield HTTPClient.GetRequest(`${crmEndPoints.FETCH_AGENT_INFO(agentId)}`);
    yield put(fetchAgentInfoSuccess(response.data));
    // message.success('Agent info fetched successfully');
  } catch (error: any) {
    yield* handleError(error, fetchAgentInfoFailure, '');
  }
}
// Add disposition
function* addDispositionSaga(action: PayloadAction<IAddDispositionRequest>) {
  try {
    const {
      borrowerId,
      dispositionId,
      remarks,
      yucollectUserId,
      agentVerificationKey,
      additionalFields,
      campaignId,
    } = action.payload;

    const selectCRM = (state: RootReducerState) => state.CRM;
    const { acwStartTime } = yield select(selectCRM);

    const response: AxiosResponse<{ message: string }> =
      yield HTTPClient.PostRequest(`${crmEndPoints.ADD_DISPOSITION()}`, {
        remarks,
        borrowerId,
        dispositionId,
        yucollectUserId,
        agentVerificationKey,
        additionalFields,
        campaignId,
        acwStartTime,
      });

    yield put(setLatestSubDispositionId(dispositionId));
    yield put(setIsAgentInManualCall(false));
    yield put(addDispositionSuccess(response.data));

    yield put(clearAcwStartTime());
    sessionStorage.removeItem('acwStartTime');

    const selectUserConfiguration = (state: RootReducerState) =>
      state.userConfiguration;
    const { entityId: AGENCY_ID = '' } = yield select(selectUserConfiguration);

    // Call interaction history
    const fetchInteractionHistoryAction = {
      type: fetchInteractionHistory.type,
      payload: {
        borrowerId,
      },
    };
    yield put(fetchInteractionHistoryAction); // Dispatch the action

    // Reset activity history filters after successful disposition
    yield put(resetActivityHistoryFilters());

    // Call best & last disposition
    const fetchBestAndLastDispositionAction = {
      type: fetchBestAndLastDisposition.type,
      payload: {
        borrowerId,
        entityId: AGENCY_ID,
      },
    };
    yield put(fetchBestAndLastDispositionAction); // Dispatch the action

    // message.success(response.data.message);
  } catch (error: any) {
    // Get error message and details
    const errorMessage =
      error?.response?.data?.message || 'Failed to add disposition';
    const errorDetails = error?.response?.data?.details || 'Please try again';

    // Update error state and trigger the UI error modal
    yield put(addDispositionFailure(error));
    yield put(
      triggerErrorModal({
        message: errorMessage,
        subMessage: errorDetails,
      })
    );
  }
}
// Fetch all dispositions
function* fetchDispositionsSaga() {
  try {
    const response: AxiosResponse<IFetchDispositionsResponse> =
      yield HTTPClient.GetRequest(`${crmEndPoints.FETCH_DISPOSITIONS()}`, {
        headers: {
          channel: 'CALL',
        },
      });
    yield put(fetchDispositionsSuccess(response.data));
    // message.success('Dispositions fetch successful');
  } catch (error: any) {
    yield* handleError(error, fetchDispositionsFailure, '');
  }
}
// Fetch disposition wise summary
function* fetchDispositionWiseSummarySaga(
  action: PayloadAction<IFetchDispositionWiseSummaryRequest>
) {
  try {
    const {
      agentId,
      campaignId,
      columns,
      limit = 10,
      offset = 0,
      dispositionIds,
    } = action.payload;
    const response: AxiosResponse<IFetchDispositionWiseSummaryResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.DISPOSITION_WISE_SUMMARY(
          agentId,
          campaignId,
          limit,
          offset
        )}`,
        {
          filters: { columns },
          dispositionIds,
        }
      );
    yield put(fetchDispositionWiseSummarySuccess(response.data));
    // message.success('Dispositions wise summary fetched successful');
  } catch (error: any) {
    yield* handleError(error, fetchDispositionWiseSummaryFailure, '');
  }
}
// Fetch interaction history
function* fetchInteractionHistorySaga(
  action: PayloadAction<IFetchInteractionHistoryRequest>
) {
  try {
    const { limit = 100, offset = 0 } = action.payload;
    const response: AxiosResponse<IFetchInteractionHistoryResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.INTERACTION_HISTORY(limit, offset)}`,
        action.payload
      );
    yield put(fetchInteractionHistorySuccess(response.data));
    // message.success('Activity history fetched successful');
  } catch (error: any) {
    yield* handleError(error, fetchInteractionHistoryFailure, '');
  }
}
// Fetch follow up time ranges
function* fetchFollowUpTimeRangesSaga(
  action: PayloadAction<IFetchFollowUpTimeRangesRequest>
) {
  try {
    const { agentId, campaignId } = action.payload;
    const response: AxiosResponse<IFetchFollowUpTimeRangesResponse[]> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.GET_FOLLOW_UP_TIME_RANGES(agentId, campaignId)}`
      );
    yield put(fetchFollowUpTimeRangesSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchFollowUpTimeRangesFailure, '');
  }
}

// Fetch follow up current time
function* fetchFollowUpCurrentTimeSaga() {
  try {
    const response: AxiosResponse<IFetchFollowUpTimeRangesResponse> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.GET_FOLLOW_UP_CURRENT_TIME()}`
      );
    yield put(fetchFollowUpCurrentTimeSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchFollowUpCurrentTimeFailure, '');
  }
}

// Add alternate number
function* addAlternateNumberSaga(
  action: PayloadAction<IAddAlternateNumberRequest>
) {
  try {
    const { borrowerId, mobileNumber, agentId } = action.payload;
    const response: AxiosResponse<IAddAlternateNumberResponse> =
      yield HTTPClient.PostRequest(`${crmEndPoints.ADD_ALTERNATE_NUMBER()}`, {
        borrowerId,
        mobileNumber,
        agentId,
      });
    yield put(addAlternateNumberSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, addAlternateNumberFailure, '');
  }
}
// Update alternate number
function* updateAlternateNumberSaga(
  action: PayloadAction<IUpdateAlternateNumberRequest>
) {
  try {
    const { borrowerId, oldMobileNumber, newMobileNumber, agentId } =
      action.payload;
    const response: AxiosResponse<IUpdateAlternateNumberResponse> =
      yield HTTPClient.PutRequest(`${crmEndPoints.ADD_ALTERNATE_NUMBER()}`, {
        borrowerId,
        oldMobileNumber,
        newMobileNumber,
        agentId,
      });
    yield put(updateAlternateNumberSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, updateAlternateNumberFailure, '');
  }
}
// Get remark templates
function* getRemarkTemplatesSaga(
  action: PayloadAction<{ subDispositionId: string }>
) {
  try {
    const { subDispositionId } = action.payload;
    const response: AxiosResponse<IGetRemarkTemplatesResponse[]> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.GET_REMARK_TEMPLATES(subDispositionId)}`
      );
    yield put(getRemarkTemplatesSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getRemarkTemplatesFailure, '');
  }
}
// Fetch missed calls
function* fetchMissedCallsSaga(
  action: PayloadAction<IFetchMissedCallsRequest>
) {
  try {
    const { startDate, endDate, searchValue, limit, offset, filters } =
      action.payload;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (searchValue) params.append('searchValue', searchValue);
    if (limit) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    const body = { filters: filters ?? { columns: [] } };
    const response: AxiosResponse<IFetchMissedCallsResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.FETCH_MISSED_CALLS()}?${params.toString()}`,
        body
      );
    yield put(fetchMissedCallsSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchMissedCallsFailure, '');
  }
}

// Fetch missed calls distinct values for filters
function* fetchMissedCallsDistinctSaga(
  action: PayloadAction<IFetchMissedCallsDistinctRequest>
) {
  try {
    const { columnName, filters } = action.payload;
    const response: AxiosResponse<(string | MissedCallDistinctItemWithId)[]> =
      yield HTTPClient.PostRequest(
        crmEndPoints.FETCH_MISSED_CALLS_DISTINCT(columnName),
        filters
      );
    yield put(fetchMissedCallsDistinctSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchMissedCallsDistinctFailure, '');
  }
}

// Barge call saga
function* callBargingSaga(action: PayloadAction<ICallBargingRequest>) {
  try {
    const { email } = action.payload;
    const response: AxiosResponse<ICallBargingResponse> =
      yield HTTPClient.PutRequest(crmEndPoints.BARGING_API(email));
    yield put(callBargingSuccess(response.data));
    message.success('Call Barging initiated successfully');
  } catch (error: any) {
    yield* handleError(
      error,
      callBargingFailure,
      'Failed to initiate barge call'
    );
  }
}

// Fetch previous or next borrower saga
function* fetchPreviousOrNextBorrowerSaga(
  action: PayloadAction<IFetchPreviousOrNextBorrowerRequest>
) {
  try {
    const { agentId, borrowerId, campaignId, direction, limit } =
      action.payload;
    const response: AxiosResponse<IFetchPreviousOrNextBorrowerResponse> =
      yield HTTPClient.GetRequest(
        crmEndPoints.FETCH_PREVIOUS_OR_NEXT_BORROWER(
          borrowerId,
          direction,
          agentId,
          campaignId,
          limit
        )
      );
    yield put(fetchPreviousOrNextBorrowerSuccess(response.data));
  } catch (error: any) {
    yield* handleError(
      error,
      fetchPreviousOrNextBorrowerFailure,
      'Failed to fetch borrower'
    );
  }
}

// Fetch priority tasks
function* fetchPriorityTasksSaga(
  action: PayloadAction<IFetchPriorityTasksRequest>
) {
  try {
    const {
      type,
      startDate,
      endDate,
      columns,
      limit = 10,
      offset = 0,
      searchValue,
    } = action.payload;
    const response: AxiosResponse<IFetchPriorityTasksResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.FETCH_PRIORITY_TASKS()}?limit=${limit}&offset=${offset}&`,
        { type, startDate, endDate, columns, searchValue }
      );
    yield put(fetchPriorityTasksSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchPriorityTasksFailure, '');
  }
}

// Fetch priority tasks summary
function* fetchPriorityTasksSummarySaga(
  action: PayloadAction<IFetchPriorityTasksSummaryRequest>
) {
  try {
    const response: AxiosResponse<PriorityTasksSummary> =
      yield HTTPClient.PostRequest(
        crmEndPoints.FETCH_PRIORITY_TASKS_SUMMARY(),
        action.payload
      );
    yield put(fetchPriorityTasksSummarySuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchPriorityTasksSummaryFailure, '');
  }
}

// Fetch priority tasks distinct values for filters
function* fetchPriorityTasksDistinctSaga(
  action: PayloadAction<IFetchPriorityTasksDistinctRequest>
) {
  try {
    const response: AxiosResponse<string[]> = yield HTTPClient.PostRequest(
      crmEndPoints.FETCH_PRIORITY_TASKS_DISTINCT(),
      action.payload
    );
    yield put(fetchPriorityTasksDistinctSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchPriorityTasksDistinctFailure, '');
  }
}

// Fetch WhatsApp messages list
function* fetchWhatsappMessagesSaga(
  action: PayloadAction<IFetchWhatsappMessagesRequest>
) {
  try {
    const { fromDate, toDate, searchValue = '', page, size } = action.payload;
    const response: AxiosResponse<IWhatsappMessagesResponse> =
      yield HTTPClient.GetRequest(
        whatsappEndPoints.GET_WHATSAPP_MESSAGES(
          fromDate,
          toDate,
          searchValue,
          page,
          size
        )
      );
    yield put(fetchWhatsappMessagesSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchWhatsappMessagesFailure, '');
  }
}

// Fetch WhatsApp badge count
function* fetchWhatsappBadgeCountSaga(action: PayloadAction<{ date: string }>) {
  try {
    const response: AxiosResponse<IFetchWhatsappBadgeCountResponse> =
      yield HTTPClient.GetRequest(
        whatsappEndPoints.GET_BADGE_COUNT(action.payload.date)
      );
    yield put(fetchWhatsappBadgeCountSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, fetchWhatsappBadgeCountFailure, '');
  }
}

// Watcher Saga
export function* watchCRMSagas() {
  yield takeLatest(fetchCampaignsByAgent.type, fetchCampaignsByAgentSaga);
  yield takeLatest(saveCampaign.type, saveCampaignSaga);
  yield takeLatest(
    fetchBorrowersInCurrentCampaign.type,
    fetchBorrowersInCurrentCampaignSaga
  );
  yield takeLatest(searchInCrmDataGrid.type, searchInCrmDataGridSaga);
  yield takeLatest(
    getUniqueValuesByColumnName.type,
    getUniqueValuesByColumnNameSaga
  );
  yield takeLatest(searchInFilterByValues.type, searchInFilterByValuesSaga);
  yield takeLatest(clickToCall.type, clickToCallSaga);
  yield takeLatest(fetchBorrowerInfo.type, fetchBorrowerInfoSaga);
  yield takeLatest(fetchAdditionalInfo.type, fetchAdditionalInfoSaga);
  yield takeLatest(fetchAgentInfo.type, fetchAgentInfoSaga);
  yield takeLatest(addDisposition.type, addDispositionSaga);
  yield takeLatest(fetchDispositions.type, fetchDispositionsSaga);
  yield takeLatest(
    fetchBestAndLastDisposition.type,
    fetchBestAndLastDispositionsSaga
  );

  yield takeLatest(
    fetchDispositionWiseSummary.type,
    fetchDispositionWiseSummarySaga
  );
  yield takeLatest(fetchInteractionHistory.type, fetchInteractionHistorySaga);
  yield takeLatest(fetchFollowUpTimeRanges.type, fetchFollowUpTimeRangesSaga);
  yield takeLatest(fetchFollowUpCurrentTime.type, fetchFollowUpCurrentTimeSaga);
  yield takeLatest(addAlternateNumber.type, addAlternateNumberSaga);
  yield takeLatest(updateAlternateNumber.type, updateAlternateNumberSaga);
  yield takeLatest(getRemarkTemplates.type, getRemarkTemplatesSaga);
  yield takeLatest(fetchMissedCalls.type, fetchMissedCallsSaga);
  yield takeLatest(fetchMissedCallsDistinct.type, fetchMissedCallsDistinctSaga);
  yield takeLatest(callBarging.type, callBargingSaga);
  yield takeLatest(
    fetchPreviousOrNextBorrower.type,
    fetchPreviousOrNextBorrowerSaga
  );
  yield takeLatest(fetchPriorityTasks.type, fetchPriorityTasksSaga);
  yield takeLatest(
    fetchPriorityTasksSummary.type,
    fetchPriorityTasksSummarySaga
  );
  yield takeLatest(
    fetchPriorityTasksDistinct.type,
    fetchPriorityTasksDistinctSaga
  );
  yield takeLatest(fetchWhatsappMessages.type, fetchWhatsappMessagesSaga);
  yield takeLatest(fetchWhatsappBadgeCount.type, fetchWhatsappBadgeCountSaga);
}
